import { useUserId } from "@/api/auth";
import { myClaimQuery } from "@/api/claims";
import { payoutDetailsForMemberQuery } from "@/api/me";
import { Lightbox } from "@/components/Lightbox";
import { PageInset } from "@/components/PageInset";
import { TokenAmount } from "@/components/TokenAmount";
import { Button } from "@/components/ui/Button";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { TopNavigation, TopNavigationBackButton } from "@/components/ui/TopNavigation";
import { AddAttachmentModal } from "@/pages/(app)/claims/[claimId]/AddAttachmentModal";
import { RequestPayoutNowModal } from "@/pages/(app)/claims/[claimId]/RequestPayoutNowModal";
import { ClaimStatusBadge } from "@/pages/(app)/claims/ClaimStatusBadge";
import { formatDate } from "@/utils/date";
import { formatCents } from "@common/utils/i18n";
import { Box, Flex, Img, Span, Text } from "@givenwell/components";
import { PlusIcon } from "@givenwell/core-solid-icons";
import { Claim } from "@givenwell/marketplace-api";
import { colors } from "@givenwell/theme";
import { useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, useCanGoBack, useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";

type ClaimWithFunding = Claim & { funding_source_id?: number };

export const Route = createFileRoute("/_auth/_app/claims/$claimId")({
  component: Page,
});

const route = getRouteApi("/_auth/_app/claims/$claimId");

function Page() {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { claimId } = route.useParams();
  const canGoBack = useCanGoBack();
  const userId = useUserId();
  const { data } = useSuspenseQuery(myClaimQuery(claimId));
  const { data: settings } = useQuery(payoutDetailsForMemberQuery);
  const [requestPayoutNowOpen, setRequestPayoutNowOpen] = useState(false);
  const [addAttachmentOpen, setAddAttachmentOpen] = useState(false);

  // Get all notes that have attachments
  const attachmentNotes = data.notes.filter(note => note.attachment_url);
  const primaryNote = attachmentNotes[0];

  if (!primaryNote) {
    return null;
  }

  const isEapFunded = data.claim.funding_source_id === "employer_funded";

  // Deferred FX: the claim is anchored in the receipt (doc) currency, and approved/paid amounts are
  // receipt-denominated — so render everything in doc_currency. (currency_code is legacy/secondary.)
  const displayCurrency = data.claim.doc_currency.toUpperCase();
  const sameAmount = data.claim.doc_amount_cents === data.claim.approved_amount_cents;
  const tokensAreEstimate = data.claim.tokens_are_estimate;
  const fullyPaid = data.claim.paid_amount_cents === data.claim.approved_amount_cents;
  const anyPaid = data.claim.paid_amount_cents > 0;

  // Collect all image URLs for the lightbox
  const allImageUrls = attachmentNotes
    .filter(note => note.attachment_mime_type?.startsWith("image"))
    .map(note => note.attachment_url!);

  return (
    <PageWrapper>
      <Box css={{ width: "100%", maxW: 1058, mx: "auto" }}>
        <TopNavigation
          title={`Claim #${data.claim.friendly_id}`}
          subtitle={data.claim.vendor_name}
          left={
            <TopNavigationBackButton
              onClick={() => {
                if (canGoBack) {
                  router.history.back();
                } else {
                  navigate({
                    to: "/claims",
                    replace: true,
                  });
                }
              }}
            />
          }
        />
        <PageInset css={{ pt: 16 }}>
          {data && (
            <Flex
              css={{
                flexDirection: "column",
                gap: 16,
                "@lg": {
                  flexDirection: "row",
                  gap: 32,
                },
              }}
            >
              <Box css={{ spaceY: 12, "@md": { width: "60%" } }}>
                {attachmentNotes.length === 1 ? (
                  <ReceiptPreview
                    filename={primaryNote.filename || ""}
                    url={primaryNote.attachment_url || ""}
                    mimeType={primaryNote.attachment_mime_type || ""}
                    allImageUrls={allImageUrls}
                  />
                ) : (
                  <AttachmentGallery attachmentNotes={attachmentNotes} allImageUrls={allImageUrls} />
                )}
                <Text css={{ fontScale: "sm" }}>
                  Receipt from <strong style={{ fontWeight: 600 }}>{data.claim.vendor_name}</strong> on{" "}
                  <strong style={{ fontWeight: 500 }}>{formatDate(new Date(data.claim.doc_date))}</strong>
                </Text>
                {(data.claim.status === "pending" || data.claim.status === "reviewing") && (
                  <Button
                    variant="tonal"
                    size="sm"
                    leftIcon={<PlusIcon style={{ width: 14, height: 14 }} />}
                    onClick={() => setAddAttachmentOpen(true)}
                    css={{ mt: 8 }}
                  >
                    Add Attachment
                  </Button>
                )}
                {data.notes.filter(n => n.note).length > 0 && (
                  <Box css={{ mt: 24 }}>
                    <Text css={{ fontScale: "sm", weight: 600, mb: 8 }}>Notes</Text>
                    <Box css={{ spaceY: 0 }}>
                      {data.notes
                        .filter(n => n.note)
                        .map((note, index, arr) => (
                          <Box
                            key={note.note_id}
                            css={{
                              fontScale: "sm",
                              py: 8,
                              borderTop: `1px solid ${colors.outline}`,
                              "&:last-child":
                                arr.length > 1
                                  ? {
                                      borderBottom: `1px solid ${colors.outline}`,
                                    }
                                  : {},
                            }}
                          >
                            <Text css={{ color: colors.text2 }}>{note.note}</Text>
                            <Text
                              css={{
                                fontScale: "xs",
                                color: colors.text3,
                                mt: 2,
                              }}
                            >
                              {note.created_by === userId ? note.created_by_name : "Givenwell"} &middot;{" "}
                              {formatDate(new Date(note.created_at))}
                            </Text>
                          </Box>
                        ))}
                    </Box>
                  </Box>
                )}
              </Box>
              <Flex css={{ flexDirection: "column", gap: 16, flex: "1 0 0px" }}>
                <Flex>
                  <Text css={{ fontScale: "sm" }}>Status</Text>
                  <Box css={{ flex: "1 0 0px" }} />
                  <ClaimStatusBadge
                    status={data.claim.status}
                    hasUnpaidTokens={data.claim.paid_amount_cents < data.claim.approved_amount_cents}
                  />
                </Flex>
                {!sameAmount ? (
                  <Box css={{ spaceY: 4 }}>
                    <Flex>
                      <Text css={{ fontScale: "sm" }}>Original Amount</Text>
                      <Box css={{ flex: "1 0 0px" }} />
                      <Text css={{ weight: 500 }}>{formatCents(data.claim.doc_amount_cents, displayCurrency)}</Text>
                    </Flex>
                    <Hint>The amount originally submitted by you.</Hint>
                  </Box>
                ) : null}
                {!isEapFunded && data.claim.status === "approved" && (
                  <>
                    <Box css={{ spaceY: 4 }}>
                      <Flex>
                        <Text
                          css={{
                            fontScale: "sm",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          {tokensAreEstimate ? "Estimated Points" : "Quoted Points"}
                        </Text>
                        <Box css={{ flex: "1 0 0px" }} />
                        <Span css={{ weight: 500 }}>
                          <TokenAmount amount={data.claim.token_amount} />
                        </Span>
                      </Flex>
                      <Hint>
                        {tokensAreEstimate
                          ? "Estimate only. Confirmed once paid."
                          : "Total points deducted when this claim is fully paid."}
                      </Hint>
                    </Box>
                  </>
                )}
                <Box css={{ spaceY: 4 }}>
                  <Flex>
                    <Text css={{ fontScale: "sm" }}>{sameAmount ? "Claim" : "Approved"} Amount</Text>
                    <Box css={{ flex: "1 0 0px" }} />
                    <Text css={{ weight: 500 }}>{formatCents(data.claim.approved_amount_cents, displayCurrency)}</Text>
                  </Flex>
                  <Hint>
                    The amount{" "}
                    {data.claim.status === "pending"
                      ? "that you've claimed for"
                      : "approved by Givenwell to be paid out"}
                    .
                  </Hint>
                </Box>
                {data.claim.status === "approved" || data.claim.status === "paid" ? (
                  <>
                    {anyPaid && (
                      <Box css={{ spaceY: 4 }}>
                        <Flex>
                          <Text css={{ fontScale: "sm" }}>Paid Amount</Text>
                          <Box css={{ flex: "1 0 0px" }} />
                          <Text css={{ weight: 500 }}>
                            {formatCents(data.claim.paid_amount_cents, displayCurrency)}
                          </Text>
                        </Flex>
                        <Hint>
                          The amount that has been paid into your bank account
                          {fullyPaid ? "" : ", so far"}.
                        </Hint>
                      </Box>
                    )}
                    {!fullyPaid && (
                      <Box css={{ spaceY: 4 }}>
                        <Flex>
                          <Text css={{ fontScale: "sm" }}>Outstanding Amount</Text>
                          <Box css={{ flex: "1 0 0px" }} />
                          <Text css={{ weight: 500 }}>
                            {formatCents(
                              Math.max(0, data.claim.approved_amount_cents - data.claim.paid_amount_cents),
                              displayCurrency,
                            )}
                          </Text>
                        </Flex>
                        <Hint>The amount still to be paid into your bank account.</Hint>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    {!isEapFunded && (
                      <Box css={{ spaceY: 4 }}>
                        <Flex>
                          <Text css={{ fontScale: "sm" }}>Points</Text>
                          <Box css={{ flex: "1 0 0px" }} />
                          <Span css={{ weight: 500 }}>
                            <TokenAmount amount={data.claim.token_amount} />
                          </Span>
                        </Flex>
                        <Hint>The number of points we expect this claim to cost.</Hint>
                      </Box>
                    )}
                  </>
                )}
                {!isEapFunded && !!settings?.beneficiary_id && !fullyPaid && data.claim.status === "approved" && (
                  <>
                    <Button variant="filled" onClick={() => setRequestPayoutNowOpen(true)}>
                      Request Payment Now
                    </Button>
                    <RequestPayoutNowModal
                      key={claimId}
                      open={requestPayoutNowOpen}
                      onOpenChange={setRequestPayoutNowOpen}
                      claimId={claimId}
                    />
                  </>
                )}
                {isEapFunded && data.claim.status === "approved" && !fullyPaid && (
                  <Hint>Your employer-funded reimbursement will be paid into your bank account shortly.</Hint>
                )}
                {(data.claim.status === "approved" || data.claim.status === "paid") && (
                  <Box
                    css={{
                      mt: 16,
                      pt: 16,
                      borderTop: `1px solid ${colors.outline}`,
                    }}
                  >
                    <Text css={{ fontScale: "sm", weight: 600, mb: 8 }}>Payments we've made to you</Text>
                    {data.payments.length === 0 ? (
                      <Text css={{ fontScale: "sm", color: colors.text2 }}>No payments</Text>
                    ) : (
                      <Box>
                        {data.payments.map(payment => (
                          <Flex
                            key={payment.payment_id}
                            css={{
                              justifyContent: "space-between",
                              alignItems: "center",
                              py: 8,
                              borderBottom: `1px solid ${colors.outline}`,
                              "&:last-child": { borderBottom: "none" },
                            }}
                          >
                            <Text css={{ fontScale: "sm", weight: 500 }}>
                              {formatCents(
                                payment.received_amount_cents ?? payment.payment_amount_cents,
                                payment.received_currency ?? payment.payment_currency,
                              )}
                            </Text>
                            <Text css={{ fontScale: "sm", color: colors.text2 }}>
                              {formatDate(new Date(payment.paid_at))}
                            </Text>
                          </Flex>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Flex>
            </Flex>
          )}
          <AddAttachmentModal
            open={addAttachmentOpen}
            onOpenChange={setAddAttachmentOpen}
            claimId={claimId}
            onSuccess={() => {
              queryClient.invalidateQueries(myClaimQuery(claimId));
            }}
          />
        </PageInset>
      </Box>
    </PageWrapper>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <Text css={{ fontScale: "xs", color: colors.text2, mt: -2 }}>{children}</Text>;
}

type ClaimNote = {
  note_id: string;
  filename?: string | null;
  attachment_url?: string | null;
  attachment_mime_type?: string | null;
};

function AttachmentGallery({
  attachmentNotes,
  allImageUrls,
}: {
  attachmentNotes: ClaimNote[];
  allImageUrls: string[];
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  function handleOpenLightbox(url: string) {
    const index = allImageUrls.indexOf(url);
    if (index >= 0) {
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  }

  return (
    <>
      <Flex css={{ gap: 8, flexWrap: "wrap" }}>
        {attachmentNotes.map((note, index) => (
          <Box
            key={note.note_id}
            css={{
              width: attachmentNotes.length <= 2 ? "calc(50% - 4px)" : "calc(33.33% - 6px)",
              aspectRatio: 1,
              overflow: "hidden",
              borderRadius: 8,
              position: "relative",
              backgroundColor: colors.warmGrey,
              cursor: note.attachment_mime_type?.startsWith("image") ? "pointer" : "default",
            }}
            onClick={() => {
              if (note.attachment_mime_type?.startsWith("image") && note.attachment_url) {
                handleOpenLightbox(note.attachment_url);
              }
            }}
          >
            {note.attachment_mime_type?.startsWith("image") ? (
              <Img
                src={note.attachment_url || ""}
                alt=""
                css={{
                  objectFit: "cover",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              />
            ) : (
              <Text
                css={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "100%",
                  align: "center",
                  fontScale: "xs",
                  px: 4,
                }}
              >
                {note.filename}
              </Text>
            )}
          </Box>
        ))}
      </Flex>
      <Lightbox
        images={allImageUrls}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        defaultIndex={lightboxIndex}
        useImageOptimization={false}
      />
    </>
  );
}

function ReceiptPreview({
  url,
  mimeType,
  filename,
  allImageUrls,
}: {
  url: string;
  mimeType: string;
  filename: string;
  allImageUrls: string[];
}) {
  const [imageZoomed, setImageZoomed] = useState(false);

  function handleZoomImage() {
    setImageZoomed(true);
  }

  let preview = (
    <>
      <Text
        css={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "100%",
          align: "center",
        }}
      >
        {filename}
      </Text>
      <embed src={url} type={mimeType} height="100%" width="100%" style={{ position: "absolute", inset: 0 }}></embed>
    </>
  );
  if (mimeType.startsWith("image")) {
    preview = (
      <Img
        src={url}
        alt=""
        css={{
          objectFit: "contain",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      />
    );
  }
  return (
    <>
      <Box
        css={{
          aspectRatio: 4 / 3,
          overflow: "hidden",
          borderRadius: 12,
          position: "relative",
          backgroundColor: colors.warmGrey,
          cursor: "pointer",
        }}
        onClick={() => {
          if (mimeType.startsWith("image")) {
            handleZoomImage();
          }
        }}
      >
        {preview}
      </Box>
      <Lightbox
        images={allImageUrls.length > 0 ? allImageUrls : [url]}
        open={imageZoomed}
        onOpenChange={setImageZoomed}
        defaultIndex={0}
        useImageOptimization={false}
      />
    </>
  );
}
